import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { DocFiles } from "@/components/documents/DocFiles";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const blank = { type_id: "", title: "", doc_date: "", vehicle_id: NONE, notes: "", station_id: "" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const DocumentDialog = ({ item, types, stations, vehicles, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [existing, setExisting] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item) { setF({ ...blank, station_id: stations[0]?.id || "", ...item, doc_date: item.doc_date || "", vehicle_id: item.vehicle_id || NONE }); setExisting(item.files || []); setFiles([]); }
  }, [item, stations]);
  if (!item) return null;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const station = isAdmin ? f.station_id : stations[0]?.id;

  const removeExisting = async (file) => {
    if (!window.confirm("Eliminare questo allegato?")) return;
    try { await api.delete(`/documents/files/${file.id}`); setExisting(existing.filter((x) => x.id !== file.id)); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    if (!item.id && !files.length && !window.confirm("Nessun allegato selezionato. Salvare comunque?")) return;
    setSaving(true);
    try {
      const payload = { ...f, doc_date: f.doc_date || null, vehicle_id: f.vehicle_id === NONE ? null : f.vehicle_id };
      const { data } = item.id ? await api.put(`/documents/${item.id}`, payload) : await api.post("/documents", payload);
      if (files.length) {
        const fd = new FormData();
        files.forEach((x) => fd.append("files", x));
        try { await api.post(`/documents/${data.id}/files`, fd); } catch (e) { toast.error(`Documento salvato, ma allegati non caricati: ${errMsg(e)}`); onSaved(); return; }
      }
      toast.success("Documento salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="document-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? "Modifica documento" : "Nuovo documento"}</DialogTitle>
          <DialogDescription>Allegati PDF o foto, salvati su Google Drive.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Tipologia documento">
            <Select value={f.type_id || undefined} onValueChange={set("type_id")}>
              <SelectTrigger data-testid="document-type-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`document-type-${t.id}`}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          {isAdmin && !item.id ? <F label="Stazione">
            <Select value={f.station_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, station_id: v, vehicle_id: NONE }))}>
              <SelectTrigger data-testid="document-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F> : <F label="Data documento"><Input type="date" value={f.doc_date} onChange={set("doc_date")} data-testid="document-date-input" /></F>}
          <F label="Titolo / riferimento"><Input value={f.title} onChange={set("title")} placeholder="Es. Polizza 2026, Fattura n. 123" data-testid="document-title-input" /></F>
          {isAdmin && !item.id && <F label="Data documento"><Input type="date" value={f.doc_date} onChange={set("doc_date")} data-testid="document-date-input" /></F>}
          <F label="Furgone (facoltativo)" span={!(isAdmin && !item.id)}>
            <PlateCombobox value={f.vehicle_id} onChange={set("vehicle_id")} testId="document-vehicle-select"
              options={[{ value: NONE, label: "— Nessun furgone" }, ...vehicles.filter((v) => v.station_id === station).map((v) => ({ value: v.id, label: `${v.plate} · ${v.model}` }))]} />
          </F>
          <F label="Allegati (PDF o foto)" span>
            {existing.length > 0 && <DocFiles files={existing} onRemove={removeExisting} testId="document-existing-files" />}
            <div className="flex flex-wrap gap-2">
              {files.map((x, i) => (
                <span key={i} className="flex items-center gap-1 rounded-md border bg-slate-50 px-2 py-1 text-xs">
                  <FileText className="h-3 w-3" />{x.name}<button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400" data-testid="document-file-label">
              <Upload className="h-4 w-4" /> Aggiungi PDF o foto
              <input type="file" accept="application/pdf,image/*" multiple className="hidden" data-testid="document-file-input" onChange={(e) => { setFiles([...files, ...e.target.files]); e.target.value = ""; }} />
            </label>
          </F>
          <F label="Note sul documento" span><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="document-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || !f.type_id} data-testid="document-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
